import { useState, useMemo } from "react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { resolveImg } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";

const CAT_LABELS = {
  breakfast: { es: "Desayuno", en: "Breakfast" },
  sandwiches: { es: "Sándwiches", en: "Sandwiches" },
  guatemalan: { es: "Guatemalteco", en: "Guatemalan" },
  soups: { es: "Sopas", en: "Soups" },
  steaks: { es: "Carnes", en: "Steaks" },
  chicken: { es: "Pollo", en: "Chicken" },
  seafood: { es: "Mariscos", en: "Seafood" },
  pizza: { es: "Pizzería", en: "Pizza" },
  salads: { es: "Ensaladas", en: "Salads" },
  appetizers: { es: "Aperitivos", en: "Appetizers" },
  especiales: { es: "Especiales", en: "Specials" },
  sides: { es: "Adicionales", en: "Sides" },
  drinks: { es: "Bebidas", en: "Drinks" },
  bakery: { es: "Panadería", en: "Bakery" },
};
const CAT_ORDER = ["guatemalan", "steaks", "chicken", "seafood", "breakfast", "sandwiches", "soups", "pizza", "appetizers", "salads", "especiales", "sides", "drinks", "bakery"];

const TAG_LABELS = {
  favorito: { es: "Favorito", en: "Favorite" },
  especial: { es: "Especial", en: "Special" },
};

export const MenuSection = ({ menu = [] }) => {
  const { lang, t, pick } = useLang();
  const hasFeatured = menu.some((m) => m.tag);
  const cats = useMemo(() => {
    const present = [...new Set(menu.map((m) => m.category))];
    const ordered = CAT_ORDER.filter((c) => present.includes(c));
    const extra = present.filter((c) => !CAT_ORDER.includes(c));
    const base = [...ordered, ...extra];
    return hasFeatured ? ["featured", ...base] : base;
  }, [menu, hasFeatured]);

  const [active, setActive] = useState(null);
  const activeCat = active && cats.includes(active) ? active : cats[0];
  const items = activeCat === "featured" ? menu.filter((m) => m.tag) : menu.filter((m) => m.category === activeCat);
  const labelFor = (c) => {
    if (c === "featured") return lang === "es" ? "★ Destacados" : "★ Featured";
    return CAT_LABELS[c] ? CAT_LABELS[c][lang] : c;
  };

  return (
    <section id="menu" className="py-24 md:py-32 bg-ink text-dough relative overflow-hidden">
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[40rem] h-[40rem] rounded-full bg-terracotta/10 blur-3xl" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <Reveal>
          <p className="overline text-maize mb-4">{t("menu_kicker")}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[0.95] max-w-2xl">{t("menu_title")}</h2>
        </Reveal>

        <div className="flex flex-wrap gap-3 mt-10 mb-12" data-testid="menu-categories">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`menu-cat-${c}`}
              className={`font-label font-bold text-sm tracking-wide rounded-full px-5 py-2.5 transition-colors duration-300 ${
                activeCat === c ? "bg-terracotta text-dough" : "bg-dough/10 text-dough/70 hover:bg-dough/20"
              }`}
            >
              {labelFor(c)}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeCat}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-2 gap-x-12 gap-y-8"
            data-testid="menu-items"
          >
            {items.map((it) => (
              <div key={it.id} className="flex gap-5 items-start border-b border-dough/15 pb-7 group">
                {it.image_url ? (
                  <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0 ring-1 ring-dough/20">
                    <img
                      src={resolveImg(it.image_url)}
                      alt={pick(it, "name")}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                ) : null}
                <div className="flex-1">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-label font-bold text-base md:text-lg text-dough flex items-center gap-2 flex-wrap">
                      {pick(it, "name")}
                      {it.tag && TAG_LABELS[it.tag] && (
                        <span
                          className={`inline-flex items-center gap-1 font-label text-[0.6rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            it.tag === "especial" ? "bg-terracotta text-dough" : "bg-maize text-ink"
                          }`}
                        >
                          ★ {TAG_LABELS[it.tag][lang]}
                        </span>
                      )}
                    </h3>
                    <span className="font-serif text-maize text-base md:text-lg whitespace-nowrap text-right">{it.price}</span>
                  </div>
                  {pick(it, "description") && (
                    <p className="font-body text-dough/60 text-sm mt-1.5 leading-relaxed">{pick(it, "description")}</p>
                  )}
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-dough/60 font-body">—</p>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
